import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Star, MapPin, Phone, Mail, Heart,
  ChevronDown, Loader2, Calendar, Share2
} from 'lucide-react';
import { 
  getBusinessById, 
  getBusinessServices, 
  getBusinessReviews, 
  getBusinessPortfolio 
} from '../services/firestore';
import { useBooking } from '../context/BookingContext';
import { 
  BusinessAccount, 
  ServiceCategory, 
  ServiceItem, 
  Review, 
  PortfolioImage 
} from '../types';
import ServiceCard from '../components/ServiceCard';
import ReviewCard from '../components/ReviewCard';

type TabType = 'services' | 'reviews' | 'portfolio' | 'details';

const ShopDetail: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { bookingState, addService, removeService, setBusiness, getTotalPrice, getTotalDuration } = useBooking();
  
  const [business, setBusiness_] = useState<BusinessAccount | null>(null);
  const [services, setServices] = useState<ServiceCategory[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('services');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const fetchBusinessData = useCallback(async () => {
    if (!businessId) return;
    
    setLoading(true);
    try {
      const [businessData, servicesData, reviewsData, portfolioData] = await Promise.all([
        getBusinessById(businessId),
        getBusinessServices(businessId),
        getBusinessReviews(businessId),
        getBusinessPortfolio(businessId)
      ]);
      
      if (businessData) {
        setBusiness_(businessData);
        setBusiness({
          businessId: businessData.id,
          name: businessData.Name,
          address: businessData.Address,
          postCode: businessData.Postcode,
          businessImage: businessData.BusinessImageUrl || '',
        });
      }
      
      setServices(servicesData);
      setReviews(reviewsData);
      setPortfolio(portfolioData);
      
      // Auto-expand first category
      if (servicesData.length > 0) {
        setExpandedCategory(servicesData[0].userType);
      }
    } catch (error) {
      console.error('Error fetching business data:', error);
    } finally {
      setLoading(false);
    }
  }, [businessId, setBusiness]);

  useEffect(() => {
    if (businessId) {
      fetchBusinessData();
    }
  }, [businessId, fetchBusinessData]);

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 4.7;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return sum / reviews.length;
  };

  const getRatingBreakdown = () => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      const rating = Math.round(review.rating) as 1 | 2 | 3 | 4 | 5;
      breakdown[rating]++;
    });
    return breakdown;
  };

  const isServiceSelected = (serviceId: string) => {
    return bookingState.selectedServices.some(s => s.service.id === serviceId);
  };

  const toggleService = (service: ServiceItem) => {
    if (isServiceSelected(service.id)) {
      removeService(service.id);
    } else {
      addService(service);
    }
  };

  const handleCheckAvailability = () => {
    if (bookingState.selectedServices.length > 0) {
      navigate(`/booking/${businessId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-prim-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-prim-background flex flex-col items-center justify-center">
        <h2 className="text-xl font-semibold text-prim-text mb-2">Business not found</h2>
        <button 
          onClick={() => navigate('/explore')}
          className="text-primary hover:underline"
        >
          Back to Explore
        </button>
      </div>
    );
  }

  const tabs: { id: TabType; label: string }[] = [
    { id: 'services', label: 'Services' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'details', label: 'Details' },
  ];

  return (
    <div className="min-h-screen bg-prim-background pb-24">
      {/* Hero Section */}
      <div className="relative h-64 sm:h-80 lg:h-96">
        {business.BusinessImageUrl ? (
          <img
            src={business.BusinessImageUrl}
            alt={business.Name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/5" />
        )}
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
        >
          <ArrowLeft size={20} className="text-prim-text" />
        </button>

        {/* Action Buttons */}
        <div className="absolute top-4 right-4 flex space-x-2">
          <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
            <Share2 size={20} className="text-prim-text" />
          </button>
          <button className="p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors">
            <Heart size={20} className="text-prim-text" />
          </button>
        </div>

        {/* Business Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-white mb-2">
            {business.Name}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm">
            <div className="flex items-center">
              <Star size={16} className="text-amber-400 fill-amber-400 mr-1" />
              <span className="font-medium">{calculateAverageRating().toFixed(1)}</span>
              <span className="ml-1">({reviews.length} reviews)</span>
            </div>
            <div className="flex items-center">
              <MapPin size={16} className="mr-1" />
              <span>{business.Postcode}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex space-x-1 overflow-x-auto no-scrollbar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Services Tab */}
          {activeTab === 'services' && (
            <motion.div
              key="services"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Search Services */}
              <div className="mb-6">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for services"
                    className="w-full pl-4 pr-4 py-3 bg-gray-50 rounded-xl border border-gray-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                  />
                </div>
              </div>

              {/* Service Categories */}
              {services.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No services available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {services.map(category => (
                    <div key={category.userType} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                      <button
                        onClick={() => setExpandedCategory(
                          expandedCategory === category.userType ? null : category.userType
                        )}
                        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-prim-text">{category.userType}</span>
                        <ChevronDown 
                          size={20} 
                          className={`text-gray-400 transition-transform ${
                            expandedCategory === category.userType ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      <AnimatePresence>
                        {expandedCategory === category.userType && (
                          <motion.div
                            initial={{ height: 0 }}
                            animate={{ height: 'auto' }}
                            exit={{ height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="p-4 pt-0 space-y-3">
                              {Object.entries(category.data).map(([key, items]) => (
                                Array.isArray(items) && items.map((service: ServiceItem, idx: number) => (
                                  <ServiceCard
                                    key={service.id || `${key}-${idx}`}
                                    service={service}
                                    isSelected={isServiceSelected(service.id)}
                                    onToggle={() => toggleService(service)}
                                    index={idx}
                                  />
                                ))
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Reviews Tab */}
          {activeTab === 'reviews' && (
            <motion.div
              key="reviews"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {/* Rating Summary */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-6">
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-prim-text mb-2">
                      {calculateAverageRating().toFixed(1)}
                    </div>
                    <div className="flex items-center justify-center mb-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star 
                          key={i}
                          size={18} 
                          className={i < Math.round(calculateAverageRating()) 
                            ? 'text-amber-400 fill-amber-400' 
                            : 'text-gray-300'
                          } 
                        />
                      ))}
                    </div>
                    <p className="text-sm text-gray-500">Based on {reviews.length} reviews</p>
                  </div>
                  
                  {/* Rating Breakdown */}
                  <div className="flex-1 space-y-2">
                    {[5, 4, 3, 2, 1].map(rating => {
                      const count = getRatingBreakdown()[rating as 1|2|3|4|5];
                      const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                      return (
                        <div key={rating} className="flex items-center gap-3 text-sm">
                          <span className="w-4 text-gray-600">{rating}</span>
                          <Star size={12} className="text-amber-400 fill-amber-400" />
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-amber-400 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="w-8 text-gray-400 text-right">{Math.round(percentage)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Reviews List */}
              {reviews.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No reviews yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review, index) => (
                    <ReviewCard key={review.id} review={review} index={index} />
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Portfolio Tab */}
          {activeTab === 'portfolio' && (
            <motion.div
              key="portfolio"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              {portfolio.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No portfolio images yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {portfolio.map((image, index) => (
                    <motion.div
                      key={image.id}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      className="aspect-square rounded-xl overflow-hidden cursor-pointer group"
                      onClick={() => setSelectedImage(image.url)}
                    >
                      <img
                        src={image.url}
                        alt={image.caption || 'Portfolio'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* Details Tab */}
          {activeTab === 'details' && (
            <motion.div
              key="details"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-6"
            >
              {/* Address */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-prim-text mb-4">Address</h3>
                <div className="flex items-start text-gray-600">
                  <MapPin size={18} className="mr-3 mt-0.5 flex-shrink-0 text-primary" />
                  <div>
                    <p>{business.Address}</p>
                    <p>{business.Postcode}</p>
                    <a 
                      href={`https://maps.google.com/?q=${encodeURIComponent(business.Address + ' ' + business.Postcode)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline text-sm mt-2 inline-block"
                    >
                      View on map →
                    </a>
                  </div>
                </div>
              </div>

              {/* About */}
              {business.Description && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-semibold text-prim-text mb-4">About us</h3>
                  <p className="text-gray-600 leading-relaxed">{business.Description}</p>
                </div>
              )}

              {/* Opening Hours */}
              {business.BusinessOperatingHours && (
                <div className="bg-white rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-semibold text-prim-text mb-4">Our Schedule</h3>
                  <div className="space-y-3">
                    {business.BusinessOperatingHours.map(hours => (
                      <div key={hours.day} className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">{hours.day}</span>
                        {hours.isOpen ? (
                          <span className="text-prim-text font-medium">
                            {hours.open} - {hours.close}
                          </span>
                        ) : (
                          <span className="text-gray-400">Closed</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className="bg-white rounded-2xl p-6 border border-gray-100">
                <h3 className="font-semibold text-prim-text mb-4">Contact</h3>
                <div className="space-y-3">
                  {business.Phone && (
                    <a 
                      href={`tel:${business.Phone}`}
                      className="flex items-center text-gray-600 hover:text-primary transition-colors"
                    >
                      <Phone size={18} className="mr-3 text-primary" />
                      {business.Phone}
                    </a>
                  )}
                  {business.Email && (
                    <a 
                      href={`mailto:${business.Email}`}
                      className="flex items-center text-gray-600 hover:text-primary transition-colors"
                    >
                      <Mail size={18} className="mr-3 text-primary" />
                      {business.Email}
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Bottom Bar - Selected Services */}
      {bookingState.selectedServices.length > 0 && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-lg p-4"
        >
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">
                {bookingState.selectedServices.length} service{bookingState.selectedServices.length > 1 ? 's' : ''} • {getTotalDuration()} mins
              </p>
              <p className="text-xl font-bold text-prim-text">
                £{getTotalPrice().toFixed(2)}
              </p>
            </div>
            <button
              onClick={handleCheckAvailability}
              className="px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors flex items-center"
            >
              <Calendar size={18} className="mr-2" />
              Check Availability
              <span className="ml-2 w-6 h-6 bg-white/20 rounded-full text-sm flex items-center justify-center">
                {bookingState.selectedServices.length}
              </span>
            </button>
          </div>
        </motion.div>
      )}

      {/* Image Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={selectedImage}
              alt="Portfolio"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ShopDetail;

